/**
 * Draws the moves, lines and curves of a charstring into device space: x is scaled, and y goes
 * through the hint map. The map changes between path elements when the charstring's hint mask
 * does, so a path element waits one call in a queue until the map for its end is known. This is
 * FreeType's `cf2_glyphpath` without stem darkening, where every offset is zero and an element's
 * end meets the next one's start.
 *
 * Portions ported from FreeType (FreeType Project License).
 * Copyright 2007-2014 Adobe Systems Incorporated.
 * See THIRD-PARTY-NOTICES.md.
 */
import { HintMap } from './cffHintMap';
import { ftMulFix } from './fontMetrics';
/** What waits in the queue. */
const QUEUED_NONE = 0;
const QUEUED_LINE = 1;
const QUEUED_CURVE = 2;
/** Where the points of a queued element sit in `GlyphPath.queued`: the control points of a curve, then its end. */
const FIRST_X = 0;
const FIRST_Y = 1;
const SECOND_X = 2;
const SECOND_Y = 3;
const END_X = 4;
const END_Y = 5;
export class GlyphPath {
    pen;
    hints;
    blues;
    hintOrigin;
    initialMap;
    hintMap;
    /** The map in force when the contour began, which draws the line that closes it. */
    firstHintMap;
    /** A contour's start and the point drawn last, in character space. The path starts at the origin whatever the charstring's first move says. */
    startX = 0;
    startY = 0;
    currentX = 0;
    currentY = 0;
    currentDeviceX = 0;
    currentDeviceY = 0;
    /**
     * The element waiting to be drawn, with its points in character space: three x, y pairs for a
     * curve and only the last pair for a line. Written by `lineTo` and `curveTo` with no allocation.
     */
    queued = new Float64Array(6);
    queuedKind = QUEUED_NONE;
    isMovePending = true;
    isClosing = false;
    /** `hintOrigin` is the y where a `seac` accent starts, which the stems of the accent are relative to. */
    constructor(pen, hints, blues, hintOrigin) {
        this.pen = pen;
        this.hints = hints;
        this.blues = blues;
        this.hintOrigin = hintOrigin;
        this.initialMap = new HintMap(blues);
        this.hintMap = new HintMap(blues, this.initialMap);
        this.firstHintMap = this.hintMap.clone();
    }
    moveTo(x, y) {
        this.closeOpenPath();
        this.startX = this.currentX = x;
        this.startY = this.currentY = y;
        this.isMovePending = true;
        if (!this.hintMap.isValid || this.hints.mask.isNew)
            this.buildHintMap();
        this.firstHintMap = this.hintMap.clone();
    }
    /** A line of zero length adds nothing, unless a new hint mask begins here: its end then lands elsewhere. */
    lineTo(x, y) {
        const startsNewMap = this.hints.mask.isNew && !this.isClosing;
        if (this.currentX === x && this.currentY === y && !startsNewMap)
            return;
        this.startPendingContour();
        this.drawQueued();
        this.queued[END_X] = x;
        this.queued[END_Y] = y;
        this.queuedKind = QUEUED_LINE;
        if (startsNewMap)
            this.buildHintMap();
        this.currentX = x;
        this.currentY = y;
    }
    curveTo(x1, y1, x2, y2, x3, y3) {
        this.startPendingContour();
        this.drawQueued();
        const { queued } = this;
        queued[FIRST_X] = x1;
        queued[FIRST_Y] = y1;
        queued[SECOND_X] = x2;
        queued[SECOND_Y] = y2;
        queued[END_X] = x3;
        queued[END_Y] = y3;
        this.queuedKind = QUEUED_CURVE;
        if (this.hints.mask.isNew)
            this.buildHintMap();
        this.currentX = x3;
        this.currentY = y3;
    }
    /** Ends the open contour with a line back to its start (`cf2_glyphpath_closeOpenPath`). */
    closeOpenPath() {
        if (this.isMovePending)
            return;
        this.isClosing = true;
        this.lineTo(this.startX, this.startY);
        if (this.queuedKind !== QUEUED_NONE) {
            this.draw(this.hintMap, this.startX, this.startY, this.firstHintMap);
        }
        this.isMovePending = true;
        this.isClosing = false;
        this.queuedKind = QUEUED_NONE;
    }
    /**
     * Places and locks the stems of a counter mask (`cntrmask`) on a map of their own, so that the
     * stems of one counter group keep together when hint groups later pull them apart.
     */
    lockCounterStems(counterMask) {
        new HintMap(this.blues, this.initialMap).build(this.hints, counterMask, 0);
    }
    /** Starts a contour if a move waits. */
    startPendingContour() {
        if (!this.isMovePending)
            return;
        this.startContour();
        this.isMovePending = false;
    }
    /** Draws the element in the queue, which the caller then refills. */
    drawQueued() {
        if (this.queuedKind !== QUEUED_NONE)
            this.draw(this.hintMap, this.currentX, this.currentY, this.hintMap);
    }
    startContour() {
        // A path with no move at all synthesizes one at the origin, which also builds the first map.
        if (!this.hintMap.isValid)
            this.moveTo(this.startX, this.startY);
        this.currentDeviceX = this.deviceX(this.currentX);
        this.currentDeviceY = this.hintMap.map(this.currentY);
        this.pen.moveTo(this.currentDeviceX, this.currentDeviceY);
    }
    /**
     * Draws the queued element, then a line to `(nextX, nextY)` if the element does not end there.
     * `map` places a curve. `endMap` places the end of a line and the next point: it differs from
     * `map` only for the element that closes a contour, which ends where the contour began, by the map
     * it began with.
     */
    draw(map, nextX, nextY, endMap) {
        const { queued } = this;
        if (this.queuedKind === QUEUED_LINE) {
            this.lineToDevice(this.deviceX(queued[END_X]), endMap.map(queued[END_Y]));
        }
        else {
            const endX = this.deviceX(queued[END_X]);
            const endY = map.map(queued[END_Y]);
            this.pen.curveTo(this.deviceX(queued[FIRST_X]), map.map(queued[FIRST_Y]), this.deviceX(queued[SECOND_X]), map.map(queued[SECOND_Y]), endX, endY);
            this.currentDeviceX = endX;
            this.currentDeviceY = endY;
        }
        // The same end placed by the same map is where the pen already is.
        const endsAtNext = nextX === queued[END_X] && nextY === queued[END_Y];
        if (endsAtNext && (this.queuedKind === QUEUED_LINE || endMap === map))
            return;
        this.lineToDevice(this.deviceX(nextX), endMap.map(nextY));
    }
    /** x in device space: x is scaled and never hinted. */
    deviceX(x) {
        return ftMulFix(x, this.blues.scale);
    }
    lineToDevice(x, y) {
        if (x === this.currentDeviceX && y === this.currentDeviceY)
            return;
        this.pen.lineTo(x, y);
        this.currentDeviceX = x;
        this.currentDeviceY = y;
    }
    buildHintMap() {
        this.hintMap.build(this.hints, this.hints.mask, this.hintOrigin);
    }
}
//# sourceMappingURL=cffGlyphPath.js.map